import React, { useContext, useMemo, useState } from "react";
import {
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    Select,
    Text,
    IconButton,
    Box,
    HStack,
    useToast,
    Tag,
    TagLabel,
} from "@chakra-ui/react";
import { FaImage, FaFileAlt, FaShareAlt } from "react-icons/fa";
import TicketDetailsModal from "../TicketDetailsModal";
import ShareTicketModal from "../ShareTicketModal";
import ShareSuccessModal from "../ShareSuccessModal";
import { useNavigate } from "react-router-dom";
import { stickyColHeaderProps, stickyColCellProps } from "../../../constants/tableStyles";
import { parseApiDate } from "../../../utils/date";
import { AuthContext } from "../../../context/AuthContext";

const TicketTable = ({ tickets, status, confirmStatusChange, handleImagePreview, getShareableLink }) => {
    const { isAdmin } = useContext(AuthContext);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
    const [shareLink, setShareLink] = useState("");
    const [sortOrder, setSortOrder] = useState("desc");
    const [sortBy, setSortBy] = useState("createdAt");
    const toast = useToast();
    const navigate = useNavigate();

    const handleDetailsClick = (ticket) => {
        setSelectedTicket(ticket);
        setIsModalOpen(true);
    };

    const handleFullDetails = (id) => {
        navigate(`/details/${id}`);
      };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedTicket(null);
    };

    const truncateText = (text, maxLength = 30) => {
        return typeof text === 'undefined' ? text : (text.length > maxLength ? text.substring(0, maxLength) + "..." : text);

    };

    const handleSortBySection = () => {
        setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    };

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortBy(field);
            setSortOrder("asc");
        }
    };
    
    const sortedTickets = useMemo(() => {
        return [...tickets]
            .filter((ticket) => ticket.status === status)
            .sort((a, b) => {
                if (sortOrder === "asc") {
                    return a[sortBy] > b[sortBy] ? 1 : -1;
                } else {
                    return a[sortBy] < b[sortBy] ? 1 : -1;
                }
            });
    }, [tickets, status, sortBy, sortOrder]);


    const handleShareClick = (ticket) => {
        setSelectedTicket(ticket);
        setIsShareModalOpen(true);
    };

    const confirmShare = async () => {
        if (!selectedTicket) return;

        try {
            const link = await getShareableLink(selectedTicket.id);
            if (!link) {
                throw new Error("Pogreška u generiranju poveznice.");
            }
            setShareLink(link);
            setIsShareModalOpen(false);
            setTimeout(() => {
                setIsSuccessModalOpen(true);
            }, 300);
        } catch (error) {
            toast({
                title: "Error",
                description: error.message || "Pogreška u generiranju poveznice.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    return (
        <>
            {sortedTickets.length > 0 ? (
                <Box bg="white" borderRadius="xl" border="1px solid" borderColor="gray.100" boxShadow="card" p={3}>
                <TableContainer overflowX="auto">
                    <Table variant="striped" colorScheme="gray" size="sm">
                        <Thead>
                            <Tr>
                                <Th {...stickyColHeaderProps}>#</Th>
                                <Th>Naziv</Th>
                                <Th cursor="pointer" onClick={() => handleSort("sectionName")}>
                                    Odjeljak {sortBy === "sectionName" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
                                </Th>
                                <Th cursor="pointer" onClick={() => handleSort("createdAt")}>
                                    Kreiran {sortBy === "createdAt" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
                                </Th>
                                <Th>Opis</Th>
                                {isAdmin && <Th>Status</Th>}
                                <Th>Akcije</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {sortedTickets.map((ticket, index) => (
                                <Tr key={ticket.id} _hover={{ bg: "gray.50" }}>
                                    <Td {...stickyColCellProps(index % 2 === 0)}>{index + 1}</Td>
                                    <Td onDoubleClick={() => handleFullDetails(ticket.id)} fontWeight="bold">
                                        {truncateText(ticket.name)}
                                    </Td>
                                    <Td fontWeight="bold">
                                        <Tag 
                                            size="sm"
                                            borderRadius="md"
                                            bg="blue.50"
                                            color="brand.700"
                                            border="1px solid"
                                            borderColor="blue.200"
                                            px={2}
                                            py={1}
                                            fontWeight="semibold"
                                        >
                                            <TagLabel>{ticket.sectionName}</TagLabel>
                                        </Tag>
                                    </Td>
                                    <Td>
                                        {parseApiDate(ticket.createdAt).toLocaleDateString('de-DE', { timeZone: 'Europe/Zagreb' }).replace(/\//g, '.')} |&nbsp;
                                        {parseApiDate(ticket.createdAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Zagreb' })}
                                    </Td>
                                    <Td>{truncateText(ticket.description)}</Td>
                                    {isAdmin && (
                                        <Td>
                                            <Select
                                                size="sm"
                                                minW="150px"
                                                width="100%"
                                                value={ticket.status}
                                                onChange={(e) => confirmStatusChange(ticket, parseInt(e.target.value))}
                                            >
                                                <option value="0">Prijavljen</option>
                                                <option value="10">U postupku</option>
                                                <option value="20">Otkazan</option>
                                                <option value="30">Završen</option>
                                                <option value="40">Arhiviran</option>
                                            </Select>
                                        </Td>
                                    )}
                                    <Td>
                                        <HStack spacing={2}>
                                            {ticket.imageUrl && (
                                                <Box as="span" border="1px solid #154360" borderRadius="md" p={1}>
                                                    <IconButton
                                                        aria-label="Preview Image"
                                                        icon={<FaImage />}
                                                        color="brand.700"
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleImagePreview(ticket.imageUrl)}
                                                    />
                                                </Box>
                                            )}
                                            <Box as="span" border="1px solid #154360" borderRadius="md" p={1}>
                                                <IconButton
                                                    aria-label="View Details"
                                                    icon={<FaFileAlt />}
                                                    color="brand.700"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleDetailsClick(ticket)}
                                                />
                                            </Box>
                                            <Box as="span" border="1px solid #154360" borderRadius="md" p={1}>
                                                <IconButton
                                                    aria-label="Podijeli zadatak"
                                                    icon={<FaShareAlt />}
                                                    color="brand.700"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleShareClick(ticket)}
                                                />
                                            </Box>
                                        </HStack>
                                    </Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                </TableContainer>
                </Box>
            ) : (
                <Text>Zadaci se učitavaju ili ne postoje u navedenom statusu.</Text>
            )}
            {isModalOpen && selectedTicket && <TicketDetailsModal ticket={selectedTicket} onClose={closeModal} />}
            <ShareTicketModal isOpen={isShareModalOpen} onClose={() => setIsShareModalOpen(false)} onConfirm={confirmShare} />
            <ShareSuccessModal isOpen={isSuccessModalOpen} onClose={() => setIsSuccessModalOpen(false)} shareLink={shareLink} />
        </>
    );
};

export default React.memo(TicketTable);
